export interface TimeDomain { fromMs: number; toMs: number }
export interface ChartViewport extends TimeDomain { mode: 'fit' | 'follow' | 'free' }

function domainWidth(domain: TimeDomain) {
  return Math.max(1, domain.toMs - domain.fromMs);
}

export function createChartViewport(domain: TimeDomain, windowMs?: number): ChartViewport {
  const width = Math.min(domainWidth(domain), Math.max(1, windowMs ?? domainWidth(domain)));
  return { fromMs: domain.toMs - width, toMs: domain.toMs, mode: width === domainWidth(domain) ? 'fit' : 'follow' };
}

export function clampChartViewport(viewport: ChartViewport, domain: TimeDomain, minWindowMs = 1): ChartViewport {
  const domainWidthMs = domainWidth(domain);
  const width = Math.min(domainWidthMs, Math.max(minWindowMs, viewport.toMs - viewport.fromMs));
  const maxStart = domain.toMs - width;
  const fromMs = Math.min(maxStart, Math.max(domain.fromMs, viewport.fromMs));
  return { fromMs, toMs: fromMs + width, mode: viewport.mode };
}

/** Zoom around the pointer anchor. factor > 1 zooms in, factor < 1 zooms out. */
export function zoomChartViewport(viewport: ChartViewport, factor: number, anchorMs: number, domain: TimeDomain, minWindowMs = 1): ChartViewport {
  const safeFactor = Number.isFinite(factor) && factor > 0 ? factor : 1;
  const width = Math.max(minWindowMs, (viewport.toMs - viewport.fromMs) / safeFactor);
  const ratio = (anchorMs - viewport.fromMs) / Math.max(1, viewport.toMs - viewport.fromMs);
  return clampChartViewport({ fromMs: anchorMs - width * ratio, toMs: anchorMs + width * (1 - ratio), mode: 'free' }, domain, minWindowMs);
}

export function panChartViewport(viewport: ChartViewport, deltaMs: number, domain: TimeDomain, minWindowMs = 1): ChartViewport {
  return clampChartViewport({ ...viewport, fromMs: viewport.fromMs + deltaMs, toMs: viewport.toMs + deltaMs, mode: 'free' }, domain, minWindowMs);
}

export function followChartViewport(domain: TimeDomain, cursorMs: number, windowMs: number): ChartViewport {
  const width = Math.min(domainWidth(domain), Math.max(1, windowMs));
  const toMs = Math.min(domain.toMs, Math.max(domain.fromMs + width, cursorMs));
  return { fromMs: toMs - width, toMs, mode: 'follow' };
}

